// SMS two-step sign-in (Firebase multi-factor auth). Optional: users turn it on from the
// Security screen; sign-in asks for the code only when the account has a phone enrolled.
import {
    RecaptchaVerifier, PhoneAuthProvider, PhoneMultiFactorGenerator, multiFactor,
    getMultiFactorResolver, EmailAuthProvider, reauthenticateWithCredential,
} from "firebase/auth";
import { auth } from "../firebase";

export const needsSecondFactor = err => err?.code === "auth/multi-factor-auth-required";
export const needsRecentLogin = err => err?.code === "auth/requires-recent-login";
export const resolverFor = err => getMultiFactorResolver(auth, err);

/** Invisible reCAPTCHA in `containerId`. Its tokens are single-use: clear() after each send. */
export const makeRecaptcha = containerId => new RecaptchaVerifier(auth, containerId, { size: "invisible" });

/** Send the sign-in code to an enrolled phone → verificationId */
export const sendSignInCode = (resolver, hint, verifier) =>
    new PhoneAuthProvider(auth).verifyPhoneNumber({ multiFactorHint: hint, session: resolver.session }, verifier);

export const finishSignIn = (resolver, verificationId, code) =>
    resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code)));

/** Send the code to a phone being added → verificationId */
export async function sendEnrolCode(user, phoneNumber, verifier) {
    const session = await multiFactor(user).getSession();
    return new PhoneAuthProvider(auth).verifyPhoneNumber({ phoneNumber, session }, verifier);
}

export const finishEnrol = (user, verificationId, code, label) =>
    multiFactor(user).enroll(PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code)), label || null);

export const phonesOf = user => multiFactor(user).enrolledFactors.filter(f => f.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
export const removePhone = (user, factor) => multiFactor(user).unenroll(factor);
export const confirmPassword = (user, password) => reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));

/** +61412345678 → +61 •••• ••678. Sign-in hints arrive already masked by Firebase
 *  (+*******678): those keep their own masking, shown with dots. */
export const maskPhone = p => {
    if (!p) return "";
    if (p.includes("*")) return `•••• ••${p.replace(/\*/g, "").slice(-3)}`;
    return `${p.slice(0, 3)} •••• ••${p.slice(-3)}`;
};

/** Spaces, dashes and brackets removed; a leading 0 becomes +61 (Australia) */
export function normalisePhone(raw) {
    const s = String(raw || "").replace(/[\s()-]/g, "");
    if (/^0\d{8,10}$/.test(s)) return `+61${s.slice(1)}`;
    return s;
}

export function mfaMessage(err) {
    const code = err?.code || "";
    const text = String(err?.message || "");
    if (/region|SMS unable to be sent/i.test(text)) return "Texts to this country aren't switched on for this app yet. Contact support.";
    return ({
        "auth/invalid-verification-code": "That code isn't right. Check the text message and try again.",
        "auth/code-expired": "That code has expired. Send a new one.",
        "auth/missing-code": "Enter the 6-digit code from the text message.",
        "auth/invalid-phone-number": "Enter the number in international format, e.g. +61 412 345 678.",
        "auth/missing-phone-number": "Enter a mobile number.",
        "auth/too-many-requests": "Too many attempts. Wait a few minutes, then try again.",
        "auth/quota-exceeded": "The text-message limit has been reached for now. Try again later.",
        "auth/captcha-check-failed": "This browser couldn't be verified. Reload the page and try again.",
        "auth/invalid-app-credential": "This browser couldn't be verified. Reload the page and try again.",
        "auth/unverified-email": "Verify your email address first.",
        "auth/second-factor-already-in-use": "That phone is already set up on this account.",
        "auth/maximum-second-factor-count-exceeded": "This account already has the most phones allowed.",
        "auth/wrong-password": "That password isn't right.",
        "auth/invalid-credential": "That password isn't right.",
        "auth/invalid-login-credentials": "That password isn't right.",
        "auth/user-mismatch": "That password belongs to a different account.",
        "auth/operation-not-allowed": "Two-step sign-in isn't available right now. Contact support.",
        "auth/billing-not-enabled": "Text messages can't be sent right now (the app's billing is switched off). Contact support.",
        "auth/network-request-failed": "No connection. Check your internet and try again.",
    })[code] || `Something went wrong. Please try again.${code ? ` (${code})` : ""}`;
}
